/**
 * Service class for accessing localStorage.
 */
export default function storageService() {
  /**
   * Keys used to reference data in localStorage
   */
  const TESTS_KEY = 'ngStorage-tests';
  const COUPON_KEY = 'ngStorage-coupon';
  const TREATMENT_TYPE = 'ngStorage-treatmentType';
  const USER_ID_KEY = 'user_id';

  /**
   * Retrieves a value stored as JSON in localStorage
   *
   * @param {string} key the key to retrieve from storage
   *
   * @return {any}  The value stored in localStorage, or null if localstorage is not available
   */
  function getLocalStorage(key) {
    try {
      return JSON.parse(window.localStorage?.getItem(key) || null);
    } catch (error) {
      console.error('Error getting value from local storage', error);

      return null;
    }
  }

  /**
   * Saves a value in localStorage as a JSON string
   *
   * @param {string} key The key to store the value in
   * @param {any} value  The value to be stored
   *
   * @return {void}
   */
  function setLocalStorage(key, value) {
    try {
      window.localStorage?.setItem(key, JSON.stringify(value || null));
    } catch (error) {
      console.error('Error setting value in local storage', error);
    }
  }

  /**
   * Deletes a value from the storage.
   *
   * @param key {string} The value's key.
   *
   * @return void
   */
  function removeLocalStorage(key) {
    window.localStorage?.removeItem(key);
  }

  // Initialize tests in localStorage
  if (!getLocalStorage(TESTS_KEY)) {
    setLocalStorage(TESTS_KEY, []);
  }

  return {
    /**
     * Local Storage get, set and remove
     */
    localStorage: {
      get: getLocalStorage,
      set: setLocalStorage,
      remove: removeLocalStorage,
    },

    /**
     * Retrieve selected tests data from local storage
     */
    get tests() {
      return this.localStorage.get(TESTS_KEY) ?? [];
    },

    /**
     * Store selected tests data in local storage
     */
    set tests(tests) {
      this.localStorage.set(TESTS_KEY, tests);
    },

    /**
     * Store the treatment type in local storage
     */
    set treatmentType(treatmentType) {
      this.localStorage.set(TREATMENT_TYPE, treatmentType);
    },

    /**
     * Store coupon data in local storage
     */
    set coupon(coupon) {
      this.localStorage.set(COUPON_KEY, coupon);
    },

    /**
     * Retrieve the Manage user ID placing an order from local storage
     */
    get userId() {
      return getLocalStorage(USER_ID_KEY);
    },

    /**
     * Store the Manage user ID placing an order in local storage
     */
    set userId(userId) {
      if (typeof userId === 'string' && /^[0-9]+$/.test(userId.trim())) {
        setLocalStorage(USER_ID_KEY, Number(userId));
      } else if (typeof userId === 'number' && Number.isInteger(userId)) {
        setLocalStorage(USER_ID_KEY, userId);
      } else if (userId === null) {
        removeLocalStorage(USER_ID_KEY);
      } else {
        console.error(`Attempt to store invalid user_id in local storage: ${userId} (type: ${typeof userId})`);
      }
    },

    /**
     * Set the treatment type to UTI
     */
    setUtiTreatmentType() {
      this.treatmentType = 'uti';
    },
  };
}
